import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Trash2, Undo2, Reply } from "lucide-react";
import { http } from "../lib/api";
import { BannedWords } from "./BannedWords";

const TABS = [["pending", "Pendentes"], ["approved", "Aprovados"], ["rejected", "Recusados"]];

const ReplyBox = ({ c, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(c.reply?.text || "");
  const save = async (value) => {
    await http.put(`/admin/comments/${c.id}/reply`, { text: value })
      .then(() => { toast.success(value ? "Resposta salva" : "Resposta removida"); setOpen(false); onSaved(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Falha ao salvar resposta"));
  };
  return (
    <div className="mt-3">
      {c.reply?.text && !open && (
        <div className="mb-2 ml-4 border-l-2 border-rose-600 pl-3 text-sm text-slate-300" data-testid={`admin-reply-${c.id}`}>
          <span className="font-mono-d text-xs text-rose-400">Resposta (aparece como “Equipe do site”):</span>
          <p className="whitespace-pre-line">{c.reply.text}</p>
        </div>
      )}
      {open ? (
        <div className="ml-4 space-y-2">
          <textarea rows={3} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Sua resposta — publicada como “Equipe do site”, sem seu nome" className="w-full border border-slate-800 bg-[#0d0c11] px-3 py-2 text-sm text-white outline-none focus:border-rose-500" data-testid={`reply-input-${c.id}`} />
          <div className="flex gap-2">
            <button onClick={() => save(text.trim())} className="bg-rose-600 px-3 py-2 text-xs uppercase tracking-widest text-white hover:bg-rose-500" data-testid={`reply-save-${c.id}`}>Salvar resposta</button>
            {c.reply?.text && <button onClick={() => save("")} className="px-3 py-2 text-xs uppercase tracking-widest text-rose-400 hover:bg-rose-950" data-testid={`reply-remove-${c.id}`}>Remover</button>}
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white">Cancelar</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest text-sky-300 hover:bg-white/5" data-testid={`reply-${c.id}`}>
          <Reply size={14} /> {c.reply?.text ? "Editar resposta" : "Responder"}
        </button>
      )}
    </div>
  );
};

export const CommentsModeration = ({ onPending }) => {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    http.get("/admin/comments", { params: { status } }).then((r) => {
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      onPending?.(r.data?.pending ?? 0);
    }).catch(() => {});
  }, [status, onPending]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, next) => {
    await http.patch(`/admin/comments/${id}`, { status: next })
      .then(() => { toast.success(next === "approved" ? "Comentário aprovado" : next === "rejected" ? "Comentário recusado" : "Voltou para pendentes"); load(); })
      .catch(() => toast.error("Falha ao atualizar"));
  };
  const del = async (id) => {
    if (!window.confirm("Excluir este comentário?")) return;
    await http.delete(`/admin/comments/${id}`).then(() => { toast.success("Excluído"); load(); }).catch(() => toast.error("Falha ao excluir"));
  };
  const btn = "flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest";

  return (
    <div data-testid="comments-moderation">
      <h2 className="font-serif-d text-3xl text-white">Comentários</h2>
      <p className="mb-6 mt-2 text-sm text-slate-300">Nada aparece no site antes da sua aprovação.</p>
      <BannedWords />
      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`comments-filter-${k}`} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] ${status === k ? "bg-rose-600 text-white" : "border border-slate-700 text-slate-300 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {items.length === 0 ? (
        <p className="border border-dashed border-white/10 p-10 text-center text-slate-400" data-testid="comments-empty">Nenhum comentário aqui.</p>
      ) : (
        <ul data-testid="comments-admin-list">
          {items.map((c) => (
            <li key={c.id} className="border-b border-white/5 py-5" data-testid={`admin-comment-${c.id}`}>
              <p className="font-mono-d flex flex-wrap gap-x-3 text-xs text-slate-400">
                <span className="text-white">{c.name}</span>
                <span>{new Date(c.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</span>
                <Link to={`/video/${c.media_id}`} className="truncate text-rose-400 hover:text-rose-300">em: {c.media_title}</Link>
                {c.auto_rejected && <span className="text-amber-300" data-testid={`auto-rejected-${c.id}`}>Recusado automaticamente: “{c.auto_rejected}”</span>}
              </p>
              <p className="mt-2 whitespace-pre-line text-slate-200">{c.text}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {status !== "approved" && <button onClick={() => act(c.id, "approved")} className={`${btn} bg-emerald-700 text-white hover:bg-emerald-600`} data-testid={`approve-${c.id}`}><Check size={14} /> Aprovar</button>}
                {status !== "rejected" && <button onClick={() => act(c.id, "rejected")} className={`${btn} border border-slate-700 text-slate-300 hover:text-white`} data-testid={`reject-${c.id}`}><X size={14} /> Recusar</button>}
                {status !== "pending" && <button onClick={() => act(c.id, "pending")} className={`${btn} text-slate-400 hover:text-white`} data-testid={`unmoderate-${c.id}`}><Undo2 size={14} /> Pendente</button>}
                <button onClick={() => del(c.id)} className={`${btn} text-rose-400 hover:bg-rose-950`} data-testid={`delete-comment-${c.id}`}><Trash2 size={14} /> Excluir</button>
              </div>
              <ReplyBox c={c} onSaved={load} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
